/* eslint-disable react/no-danger */
import { Component, Fragment } from 'react';
import PropTypes from 'prop-types';

import { get, unescape } from 'core/libs/lodash';
import { Helmet } from 'core/libs/helmet';

import oneLine from 'common-tags/lib/oneLine';

import scriptParser from 'core/utils/script-parser';
import { SNIPPET_VIDEO_PROVIDERS, TELEGRAM_PROVIDER } from 'core/site/constants';

import Indent from 'core/components/Wrappers/Indent';
import Video from 'core/components/Video';
import CaptionCredits from 'core/components/CaptionCredits';
import TelegramWidget from './TelegramWidget';

import styles from './index.styl';

const scriptRegex = /<script[^>]*>.*?<\/script>/g;

const embedsInitializers = [
  'APESTER.reload',
  'Playbuzz.render',
  'twttr.widgets.load',
  'instgrm.Embeds.process',
  'PlayBuzz.Feed.renderFeed',
  'RamblerQuiz.initializeDOMElements',
];

/**
 * Формируем скрипт инициализации всех поддерживаемых эмбедов (см. список
 * в `embedsInitializers`) оборачивая их в try..catch для упрощения поддержки.
 *
 * Нам все равно будут ли там ошибки или нет.
 */
const embedsInvokers = embedsInitializers
  .map(invoker => `try { ${invoker}() } catch (e) {}`)
  .join('\n');

const htmlWithUrl = (html, pageUrl) => {
  const newHTML = html.replace('data-quiz-id', match => `data-quiz-page-url="${pageUrl}"\n\t` + match);
  return newHTML;
};


class Snippet extends Component {
  static propTypes = {
    id: PropTypes.string,
    html: PropTypes.string,
    block: PropTypes.object,
    link: PropTypes.string,
    embeds: PropTypes.string,
    scripts: PropTypes.string,
    isMobile: PropTypes.bool,
  };

  constructor(props) {
    super(props);

    const {
      id,
      attributes,
    } = this.props.block;

    const {
      js,
      css,
      html,
    } = attributes;

    let snippetContent;

    const scripts = [];
    const domId = `placeholder-${id}`;
    const composedContent = oneLine`${unescape((html || '') + (css || '') + (js || ''))}`;

    /**
     * Так как скрипты могут располагаться в любой секции блока, то делаем
     * их поиск по объединенной версии контента.
     *
     * Вырезаем теги `script` обрабатываем их чтоб составить дескриптор
     * для дальнейшей отрисовки с помощью `react-helmet`.
     *
     * Контент же в свою очередь не будет содержать никаких скриптов.
     * Это позволит дедуплицировать подключение sdk эмбедов.
     *
     * Для оптимизации делаем вырезание и формирования колекции скриптов
     * за одну операцию.
     */
    const rawContent = composedContent.replace(scriptRegex, tag => {
      const tagScripts = scriptParser(tag);

      tagScripts.forEach(({ valid, attrs, content }) => {
        if (valid) {
          scripts.push({ attrs, content });
        }
      });

      return '';
    });

    /**
     * Так как эмбеды вмешиваются в подконтрольную реакту разметку,
     * то в браузере всегда контент формируем из отрисованной разметки.
     *
     * Это может быть как разметка полученная с сервера, так и разметка
     * проинициализированного эмбеда.
     */
    if (process.env.BROWSER_RUNTIME) {
      const container = document.querySelector(`#${domId}`);
      if (container) snippetContent = container.innerHTML;
    }

    /**
     * Если контента нет или мы отрисовываемся на сервере, то используем
     * контент полученный из апи.
     */
    if (!snippetContent) snippetContent = rawContent;

    this.state = {
      scripts,
      id: domId,
      html: snippetContent,
      embeds: embedsInvokers,
    };
  }
  render() {
    const {
      block,
      isMobile,
    } = this.props;

    const {
      id,
      html,
      embeds,
      scripts,
    } = this.state;

    const description = get(block, 'attributes.description');
    return (
      <Fragment>
        <div
          id={id}
          className={styles.snippet + (isMobile ? ` ${styles.mobile}` : '')}
          dangerouslySetInnerHTML={{ __html: html }}
        />
        <CaptionCredits credits={description} />
        <Helmet>
          {scripts.map(({ attrs, content }, i) => (
            <script
              key={i}
              {...attrs}
            >
              {content}
            </script>
          ))}
          {/**
          * Для того, чтобы при клиентской навигации или бесконечном скролле
          * `react-helmet` перевыполнял скрипт инициализации эмбедов,
          * используем id топика в качестве атрибута `data-id`
          * тега `<script>`.
          */}
          <script data-id={id}>{embeds}</script>
        </Helmet>
      </Fragment>
    );
  }
}

const SnippetWrapper = ({ block, parentProps }, { host }) => {
  const {
    attributes: {
      html,
      provider,
      description,
    },
  } = block;

  const topicLink = parentProps?.topic?.content?.attributes?.link;
  const withPageUrl = provider === 'rambler_quiz' && !!topicLink;

  if (provider === TELEGRAM_PROVIDER) {
    return (
      <Fragment>
        <TelegramWidget html={html} />
        <CaptionCredits credits={description} />
      </Fragment>
    );
  }

  if (~SNIPPET_VIDEO_PROVIDERS.indexOf(provider)) {
    return (
      <Fragment>
        <Video
          code={html}
          providerName={provider}
        />
        {description && <Indent top={10}>
          <CaptionCredits credits={description} />
        </Indent>}
      </Fragment>
    );
  }

  return (
    <Snippet
      block={{
        ...block,
        attributes: {
          ...block.attributes,
          ...withPageUrl && { html: htmlWithUrl(block.attributes.html, `https://${host}${topicLink}`) },
        },
      }}
    />
  );
};

SnippetWrapper.contextTypes = {
  host: PropTypes.string,
};

SnippetWrapper.propTypes = {
  block: PropTypes.object,
  parentProps: PropTypes.object,
};

export { SnippetWrapper };

export default function snippet(block, parentProps, ctx) {
  return (
    <SnippetWrapper
      block={block}
      parentProps={parentProps}
      ctx={ctx}
    />
  );
}
